import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, hotVideos, latestVideos, platforms } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = articles.find((a) => a.id === articleId);
  const allVideos = [...hotVideos, ...latestVideos];
  const relatedVideos = allVideos.filter(
    (v) => v.platform === article?.platform && v.id !== articleId
  ).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mt-4">文章未找到</h1>
            <p className="text-foreground-500 mt-2 text-sm">该文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          image: article.image,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          publisher: {
            '@type': 'Organization',
            name: '小蓝视频',
          },
        }}
      />

      <Navbar />

      <main className="pt-16">
        <article className="px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${getCategorySlug(article.category)}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                  {article.category}
                </span>
                <span className="text-xs px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full font-medium">
                  {article.platform}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.author}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 leading-tight">
                {article.title}
              </h1>
            </header>

            <div className="aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg mb-6">
                <strong>{article.summary}</strong>
              </p>
              <p className="text-foreground-700 leading-relaxed text-base">
                {article.content}
              </p>
              <p className="text-foreground-600 leading-relaxed text-base mt-4">
                小蓝视频持续为您带来最新、最热门的影视内容。我们汇聚香蕉视频、糖豆视频、茄子视频、糖心视频、苹果视频、青青草视频等平台的优质资源，
                致力于为每一位观众提供<strong>高清免费在线观看</strong>的极致体验。无论是热播<strong>电视剧</strong>、最新<strong>电影</strong>，还是精彩<strong>综艺</strong>和<strong>动漫</strong>，
                您都可以在小蓝视频找到。不缓冲、不卡顿，畅享观影乐趣。
              </p>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-heading text-foreground-900">相关推荐</h3>
                <Link
                  to={`/category/${getCategorySlug(article.category)}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 flex items-center gap-1 cursor-pointer"
                >
                  查看更多 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={video.image}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-foreground-900 text-sm group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                        {video.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-foreground-400">{video.category}</span>
                        <span className="text-xs px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded text-xs font-medium">
                          {video.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}

function getCategorySlug(category: string): string {
  const map: Record<string, string> = {
    '电影': 'dianying',
    '电视剧': 'dianshiju',
    '综艺': 'zongyi',
    '动漫': 'dongman',
    '纪录片': 'jilupian',
    '短剧': 'duanju',
    '剧评': 'dianshiju',
    '影评': 'dianying',
    '行业观察': 'dongman',
    '综艺分析': 'zongyi',
    '观影指南': 'dianying',
  };
  return map[category] || 'dianying';
}